import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Text } from '../../components/ui'
import type { SupportCenterSpecializationInput } from '../../types/supportCenterSpecialties'
import type { SupportCenterAssistance } from '../../types/supportCenterAssistances'

type ParametrosSpecFormModalProps = {
  formModal: SupportCenterSpecializationInput
  assistancesCatalog: SupportCenterAssistance[]
  formError: string | null
  submitting: boolean
  onFormChange: (next: SupportCenterSpecializationInput) => void
  onClose: () => void
  onSave: () => void
}

const ParametrosSpecFormModal: React.FC<ParametrosSpecFormModalProps> = ({
  formModal,
  assistancesCatalog,
  formError,
  submitting,
  onFormChange,
  onClose,
  onSave,
}) => {
  const toggleAssistance = (assistanceId: string) => {
    const selected = new Set(formModal.assistance_ids)
    if (selected.has(assistanceId)) {
      selected.delete(assistanceId)
    } else {
      selected.add(assistanceId)
    }
    onFormChange({ ...formModal, assistance_ids: [...selected] })
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
            {formModal.id ? 'Editar Especialidade' : 'Nova Especialidade'}
          </Text>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Fechar
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formModal.name}
              onChange={(e) => onFormChange({ ...formModal, name: e.target.value })}
              placeholder="Ex: Nutricionista"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrição
            </label>
            <textarea
              rows={3}
              value={formModal.description}
              onChange={(e) => onFormChange({ ...formModal, description: e.target.value })}
              placeholder="Descrição opcional..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assistências <span className="text-red-500">*</span>
            </label>
            {assistancesCatalog.length === 0 ? (
              <Text size="xs" color="muted">
                Cadastre assistências na aba Assistências antes de vincular.
              </Text>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 p-2 space-y-1">
                {assistancesCatalog.map((item) => {
                  const checked = formModal.assistance_ids.includes(item.id)
                  return (
                    <label
                      key={item.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssistance(item.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {item.name}
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          ({item.code})
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
            {formModal.assistance_ids.length === 0 ? (
              <Text size="xs" color="muted" className="mt-2">
                Selecione ao menos uma assistência.
              </Text>
            ) : (
              <Text size="xs" color="muted" className="mt-2">
                {formModal.assistance_ids.length} selecionada
                {formModal.assistance_ids.length === 1 ? '' : 's'}.
              </Text>
            )}
          </div>
        </div>
        {formError && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{formError}</Text>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" onClick={onSave} disabled={submitting}>
            {submitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ParametrosSpecFormModal
